import { filterToRenderStr, getStyleValue, resolveURLValue } from './svg-utils';
import { parseColor } from '../color';

// Helper function to resolve fill with gradient/pattern support
export function resolveFill(element, defs) {
  const fillValue = getStyleValue(element, 'fill', 'transparent');
  const fillOpacityValue = getStyleValue(element, 'fill-opacity', '1');

  const fill = resolveURLValue(fillValue, defs);
  const fillOpacity = parseFloat(fillOpacityValue) || 1;

  const { gradients, patterns } = defs;

  if (fill.startsWith('url(#')) {
    // This is a reference to a gradient or pattern
    const definition = gradients[fill] || patterns[fill];
    if (definition && definition.type === 'linearGradient') {
      // Determine gradient direction based on x1, y1, x2, y2
      let direction = 'to right'; // default direction
      if (definition.x1 && definition.y1 && definition.x2 && definition.y2) {
        const x1 = parseFloat(definition.x1);
        const y1 = parseFloat(definition.y1);
        const x2 = parseFloat(definition.x2);
        const y2 = parseFloat(definition.y2);

        // Calculate angle or use standard directions
        if (x1 === x2 && y1 > y2) direction = 'to top';
        else if (x1 === x2 && y1 < y2) direction = 'to bottom';
        else if (y1 === y2 && x1 > x2) direction = 'to left';
        else if (y1 === y2 && x1 < x2) direction = 'to right';
        else if (x1 < x2 && y1 > y2) direction = 'to top right';
        else if (x1 > x2 && y1 > y2) direction = 'to top left';
        else if (x1 < x2 && y1 < y2) direction = 'to bottom right';
        else if (x1 > x2 && y1 < y2) direction = 'to bottom left';
        else {
          // Calculate angle in degrees for more precise direction
          const angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
          direction = `${angle}deg`;
        }
      }

      const stops = definition.stops
        .map((stop) => {
          // Normalize offset to percentage if it's not already
          const offset = stop.offset.endsWith('%')
            ? stop.offset
            : `${parseFloat(stop.offset) * 100}%`;
          return `${stop.color} ${offset}`;
        })
        .join(', ');
      return `linear-gradient(${direction}, ${stops})`;
    } else if (definition && definition.type === 'radialGradient') {
      // Enhanced radial gradient with position parameters
      let position = 'center';
      if (definition.cx && definition.cy) {
        position = `${definition.cx} ${definition.cy}`;
      }

      const stops = definition.stops
        .map((stop) => {
          // Normalize offset to percentage if it's not already
          const offset = stop.offset.endsWith('%')
            ? stop.offset
            : `${parseFloat(stop.offset) * 100}%`;
          return `${stop.color} ${offset}`;
        })
        .join(', ');
      return `radial-gradient(circle at ${position}, ${stops})`;
    } else {
      // Return the original reference if we can't convert it
      return fill;
    }
  }

  if (fillOpacity === 1) return fill;

  const color = parseColor(fill);
  if (color && fillOpacity < 1) {
    // Apply fillOpacity to the color
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${fillOpacity})`;
  }

  return fill;
}

export function resolveStroke(element) {
  const value = getStyleValue(element, 'stroke', 'none');
  if (value === 'none') return '';
  return value;
}

// Helper function to handle style inheritance from parent SVG
function resolveWithSvgInheritance(element, styleName, defaultValue, isEmptyValue = (val) => !val) {
  let value = getStyleValue(element, styleName, defaultValue);

  // Handle inheritance from parent SVG if current value is empty/default
  if (isEmptyValue(value)) {
    let parent = element.parentElement;
    while (parent && parent.tagName !== 'svg') {
      parent = parent.parentElement;
    }
    if (parent && parent.tagName === 'svg') {
      const svgValue = getStyleValue(parent, styleName, defaultValue);
      if (!isEmptyValue(svgValue)) {
        value = svgValue;
      }
    }
  }

  return value;
}

export function resolveStrokeWidth(element) {
  const value = resolveWithSvgInheritance(
    element,
    'stroke-width',
    '0',
    (val) => !val || val === '0'
  );
  return parseFloat(value) || 0;
}

export function resolveStrokeDasharray(element) {
  return resolveWithSvgInheritance(
    element,
    'stroke-dasharray',
    '',
    (val) => !val || val === 'none'
  );
}

export function resolveStrokeDashoffset(element) {
  const value = resolveWithSvgInheritance(
    element,
    'stroke-dashoffset',
    '0',
    (val) => !val || val === '0'
  );
  return parseFloat(value) || 0;
}

export function resolveStrokeLinecap(element) {
  return getStyleValue(element, 'stroke-linecap', 'butt');
}

export function resolveStrokeLinejoin(element) {
  return getStyleValue(element, 'stroke-linejoin', 'miter');
}

export function resolveStrokeMiterlimit(element) {
  const value = getStyleValue(element, 'stroke-miterlimit', '4');
  return parseFloat(value) || 4;
}

export function resolveFillRule(element) {
  return getStyleValue(element, 'fill-rule', 'nonzero');
}

export function resolveStrokeOpacity(element) {
  const value = getStyleValue(element, 'stroke-opacity', '1');
  return parseFloat(value) || 1;
}

export function resolveFontWeight(element) {
  return getStyleValue(element, 'font-weight', 'normal');
}

export function resolveFontStyle(element) {
  return getStyleValue(element, 'font-style', 'normal');
}

export function resolveTextDecoration(element) {
  return getStyleValue(element, 'text-decoration', 'none');
}

export function resolveTextAnchor(element) {
  return getStyleValue(element, 'text-anchor', 'start');
}

export function resolveOpacity(element) {
  const value = getStyleValue(element, 'opacity', '1');

  // Parse opacity values, defaulting to 1 (fully opaque) if not specified
  const parsedOpacity = parseFloat(value);

  // Ensure the value is between 0 and 1
  return Math.min(Math.max(isNaN(parsedOpacity) ? 1 : parsedOpacity, 0), 1);
}

export function resolveRadius(element) {
  const value = getStyleValue(element, 'rx', '0');

  // Parse rx values, defaulting to 0 if not specified
  const parsedRx = parseFloat(value);

  // Ensure the value is non-negative
  return Math.max(isNaN(parsedRx) ? 0 : parsedRx, 0);
}

// Helper function to resolve filter
export function resolveFilter(element, defs) {
  const value = getStyleValue(element, 'filter', '');
  const { filters } = defs;
  if (value.startsWith('url(#')) {
    const filterDef = filters[value];
    if (filterDef) {
      return filterToRenderStr(filterDef);
    }
  }
  return null;
}
